import { useStore } from '@nanostores/react';
import { memo, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PromptEditor } from './PromptEditor';
import { SectionContainer } from './SectionContainer';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { getTeamPrompt, updateTeamPrompt } from '~/lib/.client/api/organizations';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { useStateTracked } from '~/lib/hooks/useStateTracked';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { logger } from '~/utils/logger';
import { withSpinner } from '~/utils/withSpinner';

export const KnowledgeTeam = memo(() => {
  const activeOrganization = useStore(userStore.getActiveOrganization());

  const [teamPrompt, setTeamPrompt, teamPromptChanged] = useStateTracked(() => '');
  const [loadingTeamPrompt, setLoadingTeamPrompt] = useState(true);

  const isAdmin = activeOrganization ? activeOrganization.role === 'admin' : false;

  useEffect(() => {
    if (activeOrganization && isAdmin) {
      withSpinner({
        action: async () => {
          try {
            const prompt = await getTeamPrompt({ organization: activeOrganization });
            setTeamPrompt(prompt || '', true);
          } catch (error: unknown) {
            if (error instanceof Error && error.message) {
              toast.error(error.message);
            }

            logger.error('Failed to load team prompt:', error);
            throw error;
          }
        },
        onStateUpdate: setLoadingTeamPrompt,
      });
    } else {
      setTeamPrompt('', true);
      setLoadingTeamPrompt(false);
    }
  }, [activeOrganization, isAdmin]);

  const handleTeamPromptChange = useCallback(
    async (value: string) => {
      if (!activeOrganization) {
        return;
      }

      try {
        const trimmedValue = value.trim();

        await updateTeamPrompt({
          organization: activeOrganization,
          prompt: trimmedValue,
        });

        setTeamPrompt(trimmedValue, true);

        toast.success('Team prompt updated');

        sendAnalyticsEvent({
          action: AnalyticsAction.Track,
          payload: {
            event: AnalyticsTrackEvent.TeamPromptUpdated,
            properties: {
              promptLength: trimmedValue.length,
            },
          },
        });
      } catch (error) {
        if (error instanceof Error && error.message) {
          toast.error(error.message);
        } else {
          logger.error(error);
          toast.error('Failed to update team prompt');
        }
      }
    },
    [activeOrganization],
  );

  return (
    <SectionContainer title="Knowledge" className="flex flex-col h-full min-h-0 pb-6">
      {loadingTeamPrompt ? (
        <LoadingDots text="Loading team prompt" />
      ) : (
        <PromptEditor
          value={teamPrompt}
          disabled={!activeOrganization || !isAdmin}
          saveDisabled={!teamPromptChanged || !isAdmin}
          placeholder={
            !activeOrganization
              ? 'Join a team to access team prompts'
              : !isAdmin
                ? 'Only team admins can edit team prompts'
                : 'Team prompt here...'
          }
          description="Define team-specific instructions that Bolt will follow for all team members. This helps ensure consistent behavior across your team."
          onSave={handleTeamPromptChange}
          setValue={setTeamPrompt}
          innerClassName="h-full min-h-0"
        />
      )}
    </SectionContainer>
  );
});
